/**
 * ComponentTypeMap — compile-time mapping from component type IDs to their class types.
 * 
 * The `ComponentTypeMap` interface and `ComponentTypeId` type are AUTO-GENERATED
 * by the `component-registry-plugin` Vite plugin into `ComponentTypeMap.generated.ts`.
 * Any class with an `@component()` decorator is picked up automatically.
 * 
 * This file re-exports those types and provides the `createComponentInstance` factory.
 * 
 * Usage:
 * ```typescript
 * const rig = go.getComponentData('core.CameraRig');
 * //    ^? CameraRig | null — full autocomplete, no casts
 * rig?.solo;           // boolean
 * rig?.positionDamping; // number
 * ```
 */

export type { ComponentTypeMap, ComponentTypeId } from './ComponentTypeMap.generated';
import type { ComponentTypeMap, ComponentTypeId } from './ComponentTypeMap.generated';
import { componentRegistry } from './attributes/component';

// ============================================================================
// FACTORY
// ============================================================================

/**
 * Create a component instance with proper defaults from the class definition.
 * 
 * If a decorated class exists for the typeId, a new instance is created with
 * field initializers providing defaults. The `data` object (if any) is then
 * merged via Object.assign, overriding only the fields that were serialized.
 * 
 * For unregistered type IDs (e.g., custom user components), falls back to
 * returning a shallow copy of `data` or an empty object.
 * 
 * @param typeId Component type ID (e.g., 'core.CameraRig')
 * @param data Optional serialized data to merge into the instance
 * @returns A component instance (class instance or plain object)
 */
export function createComponentInstance<K extends ComponentTypeId>(
  typeId: K,
  data?: Record<string, unknown>,
): ComponentTypeMap[K];
export function createComponentInstance(
  typeId: string,
  data?: Record<string, unknown>,
): unknown;
export function createComponentInstance(
  typeId: string,
  data?: Record<string, unknown>,
): unknown {
  const metadata = componentRegistry.get(typeId);
  
  if (metadata?.classRef) {
    // Create a proper class instance with all field defaults
    const instance = new metadata.classRef() as object;

    // Merge serialized data over defaults — skip undefined values (so class field initializers
    // are preserved when no explicit value was provided) AND skip keys that would clobber a
    // method or getter declared on the component class (components can now carry methods —
    // e.g. Tilemap.cellToWorld — and a stale/hostile serialized key must not overwrite one).
    if (data) {
      for (const key in data) {
        if (data[key] === undefined) continue;
        const desc = findPropertyDescriptor(instance, key);
        if (desc && (typeof desc.value === "function" || (desc.get && !desc.set))) continue;
        (instance as Record<string, unknown>)[key] = data[key];
      }
    }

    return instance;
  }
  
  // Fallback for unregistered types: plain object
  return data ? { ...data } : {};
}

/** Find a property descriptor on an object or anywhere up its prototype chain. */
function findPropertyDescriptor(obj: object, key: string): PropertyDescriptor | undefined {
  let o: object | null = obj;
  while (o) {
    const d = Object.getOwnPropertyDescriptor(o, key);
    if (d) return d;
    o = Object.getPrototypeOf(o);
  }
  return undefined;
}
